import React from "react";
import ReactApexChart from "react-apexcharts";
import { getNiceMax } from "../utils/chartDataUtils";
import MemberBreakdownSkeleton from "./MemberBreakdownSkeleton";

const COLOR = "#16a34a";

// SG-2164: member × outcome matrix — each member's own outcome split. Shown below the chart when the
// admin turns on "Breakdown by member" (Call Outcomes has no single number per member, so the flat
// MemberBreakdownList doesn't fit — this is a compact per-member outcome grid instead).
const CallOutcomesByMember = ({ rows, loading }) => {
  if (loading) return <MemberBreakdownSkeleton />;
  if (!rows?.length) return null;
  return (
    <div className="callOutcomesByMember">
      {rows.map(m => (
        <div key={m.memberId} className="callOutcomesByMemberRow">
          <div className="callOutcomesByMemberHeader">
            <span className="callOutcomesByMemberName">{m.name || m.memberId}</span>
            <span className="callOutcomesByMemberTotal">{m.total}</span>
          </div>
          <div className="callOutcomesByMemberOutcomes">
            {(m.outcomes || []).map(o => (
              <span key={o.outcome} className="callOutcomesByMemberChip">
                {o.outcome}: <b>{o.count}</b>
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// SG-2164: Call Outcomes is a categorical breakdown (one bar per outcome). Its inherent default is
// BAR (see getDefaultVisualization), so a null/undefined visualization renders as bar. Users can
// switch to a Line chart (x-axis hidden, outcome shown on hover — long outcome names never fit under
// ticks at small widths) or a KPI numeric summary.
const CallOutcomesWidget = ({ callOutcomes = [], visualization, memberBreakdown, breakdownLoading }) => {
  const hasBreakdown = breakdownLoading || (memberBreakdown && memberBreakdown.length > 0);

  if (!callOutcomes.length) {
    // Still surface the per-member breakdown even if the combined chart has no data to show.
    if (hasBreakdown) {
      return <CallOutcomesByMember rows={memberBreakdown} loading={breakdownLoading} />;
    }
    return <p className="callOutcomesEmpty">No call outcomes data</p>;
  }

  // Fallback label so a null/blank outcome (e.g. a disposition the team map couldn't resolve) never
  // reaches ApexCharts as a null category — passing null in xaxis.categories crashes the chart
  // ("Cannot read properties of null (reading 'length')") and white-screens the dashboard.
  const labels = callOutcomes.map(o => o.outcome || "Unknown");
  const counts = callOutcomes.map(o => o.count || 0);
  const { max: niceMax, tickAmount } = getNiceMax(Math.max(...counts, 1));

  const isKpi = visualization === "KPI";
  const isLine = visualization === "LINE";

  if (isKpi) {
    return (
      <>
        <div className="widgetCardNumericSummary">
          {callOutcomes.map((o, i) => (
            <div key={o.outcome || i} className="widgetCardNumericSummaryRow">
              <span className="widgetCardNumericSummaryLabel">{o.outcome || "Unknown"}</span>
              <span className="widgetCardNumericSummaryValue">{o.count}</span>
            </div>
          ))}
        </div>
        <CallOutcomesByMember rows={memberBreakdown} loading={breakdownLoading} />
      </>
    );
  }

  const options = {
    chart: {
      // "area" (not "line") so the connecting line renders reliably — matches RateWidget/CountWidget.
      type: isLine ? "area" : "bar",
      toolbar: { show: false },
      animations: { enabled: false },
      selection: { enabled: false },
      zoom: { enabled: false }
    },
    ...(isLine
      ? {
          stroke: { show: true, curve: "smooth", width: 2, colors: [COLOR] },
          markers: { size: 4, colors: [COLOR], strokeColors: "#fff", strokeWidth: 2, hover: { size: 5 } },
          fill: { type: "solid", opacity: 0.2 }
        }
      : {
          plotOptions: { bar: { borderRadius: 3, columnWidth: "90%", distributed: true } }
        }),
    colors: [COLOR],
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: {
      categories: labels,
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false }
    },
    yaxis: {
      min: 0,
      max: niceMax,
      tickAmount,
      labels: {
        formatter: val => Math.round(val),
        style: { fontSize: "10px", colors: "#999" }
      }
    },
    grid: {
      borderColor: "#e8ebee",
      strokeDashArray: 3,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } }
    },
    tooltip: {
      x: { show: true },
      y: { formatter: val => `${val} calls` }
    }
  };

  const series = [{ name: "Calls", data: counts }];

  // Chart fills the widget width — all outcomes fit inside the same widget size, no horizontal scroll.
  const chart = (
    // key forces a fresh chart instance on type change — react-apexcharts leaves a chart half-drawn
    // (markers only, no line) when chart.type is swapped in place on the same mounted instance.
    <ReactApexChart
      key={isLine ? "area" : "bar"}
      options={options}
      series={series}
      type={isLine ? "area" : "bar"}
      height={140}
      width="100%"
    />
  );

  return (
    <>
      <div className="callOutcomesChartWrap">{chart}</div>
      <CallOutcomesByMember rows={memberBreakdown} loading={breakdownLoading} />
    </>
  );
};

export default CallOutcomesWidget;
